import { useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Heading,
  Text,
  Table,
  Button,
  Flex,
  Badge,
  Grid,
  IconButton,
} from "@radix-ui/themes";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { DotsVerticalIcon, PlayIcon, ClockIcon } from "@radix-ui/react-icons";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import { Section } from "@radix-ui/themes";
import { DateFormatter } from "@/v2/components/formatters/DateFormatter";
import DropdownMenu, {
  DropdownMenuItem,
} from "@/components/shared/dropdown-menu/DropdownMenu";

import styles from "./AgentHome.module.scss";
import { toast } from "sonner";
import { ReleaseStatus } from "@/v2/entities/release/Release.model";
import { RatioFormatter } from "@/v2/components/formatters/RationFormatter";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import { useMutation, useQuery } from "react-query";
import { useReleases } from "@/routes/simulator/context/release";
import { SimulatorChannelIcon } from "@/routes/simulators/SimulatorChannelIcon";
import { useLiveEnabledTopics } from "@/routes/simulator/useLiveEnabledTopics";
import { Ticket03Icon } from "@/v2/assets/icons/Ticket03Icon";
import { Comment01Icon } from "@/v2/assets/icons/Comment01Icon";
import { Rotate3d } from "@/v2/assets/icons/3dRotate";
import { SidebarRight } from "@/v2/assets/icons/SidebarRight";
import { Slack } from "@/v2/assets/icons/Slack";
import { SimulatorChannel } from "@/api/simulator/simulator.model";
import { RegressionBreadcrumbs } from "./RegressionBreadcrumbs";
import { StructureCheck } from "@/v2/assets/icons/StructureCheck";

function getChannelIcon(channel: SimulatorChannel) {
  switch (channel) {
    case "autonomous_ticket_event":
      return <Ticket03Icon size={32} fill="blue" />;
    case "autonomous_quackchat":
      return <Comment01Icon size={32} fill="orange" />;
    case "copilot":
      return <SidebarRight size={32} fill="teal" />;
    case "slackbot":
      return <Slack size={32} fill="purple" />;
    case "knowledge_hub":
      return <Rotate3d size={32} fill="cyan" />;
    default:
      return null;
  }
}

export function AgentHome() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { simulators } = useReleases();

  const agent = useMemo(() => {
    return simulators?.find((s) => s.id.toString() === agentId);
  }, [simulators, agentId]);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, []);

  const getTypeInfo = useCallback((channel: string) => {
    switch (channel) {
      case "autonomous_ticket_event":
        return {
          label: "Tickets",
          className: "tickets",
        };
      case "autonomous_quackchat":
        return {
          label: "Live Chat",
          className: "liveChat",
        };
      case "helpcenter":
        return {
          label: "Help Center",
          className: "helpCenter",
        };
      case "copilot":
        return {
          label: "Copilot",
          className: "coPilot",
        };
      case "slackbot":
        return {
          label: "Slackbot",
          className: "slackbot",
        };
      case "knowledge_hub":
        return {
          label: "Knowledge Hub",
          className: "knowledgeHub",
        };
      default:
        return {
          label: "Auto-pilot",
          className: "tickets",
        };
    }
  }, []);

  const typeInfo = useMemo(() => {
    return agent ? getTypeInfo(agent.channel) : null;
  }, [agent, getTypeInfo]);

  const liveRelease = useMemo(() => {
    return agent?.releases?.find((r: any) => r.status === "published");
  }, [agent]);

  const status = useMemo(() => {
    if (liveRelease) return "Live";
    return "Training";
  }, [liveRelease]);

  const lastRelease = useMemo(() => {
    if (liveRelease?.publishedAt) {
      return formatDate(liveRelease.publishedAt);
    }
    return "Never";
  }, [liveRelease, formatDate]);

  const { count: liveTopics } = useLiveEnabledTopics(agent?.releases);

  const { getRegressionSetsBySimulatorId, runRegressionTestBySet } =
    useRegressionApi();

  const { data: regressionSets, isLoading: regressionSetsLoading } = useQuery({
    queryKey: ["regression-sets", agentId],
    queryFn: () => getRegressionSetsBySimulatorId(agentId || ""),
  });

  const accuracy = useMemo(() => {
    const autoSet = regressionSets?.find((set) => set.frequency);
    return autoSet
      ? autoSet?.mostRecentTest?.avgAccuracy
      : regressionSets?.[0]?.mostRecentTest?.avgAccuracy;
  }, [regressionSets]);

  const runTestMutation = useMutation({
    mutationKey: ["run-regression-test-by-set"],
    mutationFn: runRegressionTestBySet,
  });
  const release = useMemo(() => {
    return agent?.releases?.find((r) => r.status === ReleaseStatus.published);
  }, [agent]);

  const handleRunManually = useCallback(async (regressionSetId: string) => {
    try {
      const result = await runTestMutation.mutateAsync({
        regressionSetId: regressionSetId || "",
        releaseId: release?.id || 0,
      });

      toast(`Test ${result.regressionTestId} completed`);
      navigate(
        `/training-center/${agentId}/regression-sets/${regressionSetId}`
      );
    } catch (error) {
      toast.error("Failed to run regression test");
    }
  }, []);

  if (!agent) {
    return (
      <PageLayoutV2>
        <Flex justify="center" align="center" style={{ minHeight: "400px" }}>
          <Text size="3" color="gray">
            Agent not found
          </Text>
        </Flex>
      </PageLayoutV2>
    );
  }

  if (regressionSetsLoading) {
    return (
      <PageLayoutV2>
        <Flex justify="center" align="center" style={{ minHeight: "400px" }}>
          <Text size="3" color="gray">
            Loading...
          </Text>
        </Flex>
      </PageLayoutV2>
    );
  }

  return (
    <PageLayoutV2>
      <Flex direction="column">
        <RegressionBreadcrumbs
          agentId={agentId}
          agentName={agent.name}
          agentChannel={agent.channel}
        />
        <PageHeader
          title="Accuracy Checker"
          description="Regression testing for your production AI to ensure consistent performance and quality"
          icon={<StructureCheck size={32} fill="gray" />}
        />

        <Section size="4" pt="0">
          <Flex p="6" direction="column" gap="6" width="100%">
            {/* Metric Cards */}
            <Grid columns="4" gap="3" width="100%">
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <CardDescription className="text-sm text-muted-foreground">
                    Accuracy
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-semibold">
                    {RatioFormatter({
                      value: accuracy,
                    })}
                  </div>
                </CardContent>
              </Card>
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <CardDescription className="text-sm text-muted-foreground">
                    Status
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Badge size="2" color={status === "Live" ? "green" : "blue"}>
                    {status}
                  </Badge>
                </CardContent>
              </Card>
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <CardDescription className="text-sm text-muted-foreground">
                    Last Release
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-lg font-medium">{lastRelease}</div>
                </CardContent>
              </Card>
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <CardDescription className="text-sm text-muted-foreground">
                    Live Topics
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-lg font-medium">{liveTopics}</div>
                </CardContent>
              </Card>
            </Grid>

            {regressionSets?.length ? (
              <>
                {/* Accuracy Checkers Table */}
                <Box>
                  <Flex direction="column" gap="4">
                    <Flex direction={"row"} gap={"2"} align="center">
                      <Flex gap={"2"} align={"center"}>
                        <Text size={"3"} weight={"medium"}>
                          Accuracy Checkers
                        </Text>
                        <Badge color="gray">{regressionSets?.length ?? 0}</Badge>
                      </Flex>
                    </Flex>

                    <Table.Root>
                      <Table.Header>
                        <Table.Row className=" text-gray-500 font-normal">
                          <Table.ColumnHeaderCell className=" font-normal">
                            Test Name
                          </Table.ColumnHeaderCell>
                          <Table.ColumnHeaderCell className=" font-normal">Type</Table.ColumnHeaderCell>
                          <Table.ColumnHeaderCell className=" font-normal">
                            Tests Run
                          </Table.ColumnHeaderCell>
                          <Table.ColumnHeaderCell className=" font-normal">
                            Last Result
                          </Table.ColumnHeaderCell>
                          <Table.ColumnHeaderCell className=" font-normal">
                            Last Run
                          </Table.ColumnHeaderCell>
                          <Table.ColumnHeaderCell className=" font-normal">
                            Next Run
                          </Table.ColumnHeaderCell>
                          <Table.ColumnHeaderCell className=" font-normal" align="right">
                            Actions
                          </Table.ColumnHeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {regressionSets?.map((regressionSet) => (
                          <Table.Row
                            key={regressionSet.id}
                            className={styles.tableRow}
                            onClick={() =>
                              navigate(
                                `/training-center/${agentId}/regression-sets/${regressionSet.id}`
                              )
                            }
                          >
                            <Table.Cell>
                              <Text weight="medium">{regressionSet.name}</Text>
                            </Table.Cell>
                            <Table.Cell>
                              <Badge size="2" color="blue">
                                {regressionSet.frequency
                                  ? "Automatic"
                                  : "Manual"}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell>
                              <Text>{regressionSet.regressionTestCount}</Text>
                            </Table.Cell>
                            <Table.Cell>
                              <Badge
                                size="2"
                                color={
                                  !regressionSet.mostRecentTest?.avgAccuracy
                                    ? "gray"
                                    : regressionSet.mostRecentTest.avgAccuracy >=
                                      0.8
                                    ? "green"
                                    : regressionSet.mostRecentTest.avgAccuracy >=
                                      0.6
                                    ? "yellow"
                                    : "red"
                                }
                              >
                                {RatioFormatter({
                                  value:
                                    regressionSet.mostRecentTest?.avgAccuracy,
                                })}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell>
                              {regressionSet.mostRecentTest?.runTime ? (
                                <DateFormatter
                                  value={regressionSet.mostRecentTest?.runTime}
                                />
                              ) : (
                                <Text size="2" color="gray">
                                  Never
                                </Text>
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              {regressionSet.nextRun ? (
                                <DateFormatter
                                  value={
                                    regressionSet.nextRun
                                      ? new Date(
                                          new Date(
                                            regressionSet.nextRun
                                          ).setUTCHours(9, 0, 0, 0)
                                        ).toString()
                                      : ""
                                  }
                                />
                              ) : (
                                <Text size="2" color="gray">
                                  Never
                                </Text>
                              )}
                            </Table.Cell>
                            <Table.Cell align="right">
                              <DropdownMenu
                                items={[
                                  {
                                    label: "Configure",
                                    onClick: (e) => {
                                      e.stopPropagation();
                                      navigate(
                                        `/training-center/${agentId}/regression-sets/${regressionSet.id}/configure`
                                      );
                                    },
                                    icon: <DotsVerticalIcon />,
                                  },
                                  {
                                    label: "Run Manually",
                                    onClick: (e) => {
                                      e.stopPropagation();
                                      handleRunManually(regressionSet.id);
                                    },
                                    icon: <PlayIcon />,
                                  },
                                ]}
                                align="end"
                              >
                                <DropdownMenu.Trigger>
                                  <Button
                                    size="1"
                                    variant="ghost"
                                    color="gray"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <DotsVerticalIcon />
                                  </Button>
                                </DropdownMenu.Trigger>
                              </DropdownMenu>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Flex>
                </Box>
              </>
            ) : (
              <Flex
                justify="center"
                align="center"
                style={{ minHeight: "400px" }}
              >
                <Text size="3" color="gray">
                  No regression sets found
                </Text>
              </Flex>
            )}
          </Flex>
        </Section>
      </Flex>
    </PageLayoutV2>
  );
}
